import { supabase } from '../lib/supabase';

/**
 * Pronunciation Analytics Service
 * Handles all pronunciation analytics data operations with snake_case to camelCase conversion
 */
export const pronunciationAnalyticsService = {
  /**
   * Get pronunciation analytics for all users (admin only)
   */
  async getAllUserAnalytics(filters = {}) {
    try {
      let query = supabase?.from('pronunciation_analytics')?.select(`
          *,
          user_profiles!inner(id, full_name, email),
          plays!inner(id, title)
        `);

      // Apply filters
      if (filters?.playId) {
        query = query?.eq('play_id', filters?.playId);
      }

      if (filters?.dateFrom) {
        query = query?.gte('date', filters?.dateFrom);
      }

      if (filters?.dateTo) {
        query = query?.lte('date', filters?.dateTo);
      }

      if (filters?.minScore) {
        query = query?.gte('average_score', filters?.minScore);
      }

      query = query?.order('date', { ascending: false });

      const { data, error } = await query;

      if (error) throw error;

      // Convert snake_case to camelCase
      return data?.map(analytics => ({
        id: analytics?.id,
        userId: analytics?.user_id,
        playId: analytics?.play_id,
        date: analytics?.date,
        totalAttempts: analytics?.total_attempts,
        averageScore: analytics?.average_score,
        improvementTrend: analytics?.improvement_trend,
        commonMistakes: analytics?.common_mistakes,
        aiFeedbackSummary: analytics?.ai_feedback_summary,
        practiceStreak: analytics?.practice_streak,
        difficultyProgression: analytics?.difficulty_progression,
        createdAt: analytics?.created_at,
        updatedAt: analytics?.updated_at,
        userProfile: {
          id: analytics?.user_profiles?.id,
          fullName: analytics?.user_profiles?.full_name,
          email: analytics?.user_profiles?.email
        },
        play: {
          id: analytics?.plays?.id,
          title: analytics?.plays?.title
        }
      })) || [];
    } catch (err) {
      console.error('Error fetching user analytics:', err);
      throw err;
    }
  },

  /**
   * Get aggregated statistics across all users
   */
  async getAggregatedStats(filters = {}) {
    try {
      let query = supabase?.from('pronunciation_analytics')?.select('total_attempts, average_score, improvement_trend, practice_streak');

      if (filters?.playId) {
        query = query?.eq('play_id', filters?.playId);
      }

      if (filters?.dateFrom) {
        query = query?.gte('date', filters?.dateFrom);
      }

      if (filters?.dateTo) {
        query = query?.lte('date', filters?.dateTo);
      }

      const { data, error } = await query;

      if (error) throw error;

      if (!data || data?.length === 0) {
        return {
          totalPracticeSessions: 0,
          averageImprovementScore: 0,
          averageAccuracyRate: 0,
          averagePracticeStreak: 0
        };
      }

      // Calculate aggregated stats
      const totalSessions = data?.reduce((sum, d) => sum + d?.total_attempts, 0);
      const avgScore = data?.reduce((sum, d) => sum + parseFloat(d?.average_score), 0) / data?.length;
      const avgImprovement = data?.reduce((sum, d) => sum + parseFloat(d?.improvement_trend), 0) / data?.length;
      const avgStreak = data?.reduce((sum, d) => sum + d?.practice_streak, 0) / data?.length;

      return {
        totalPracticeSessions: totalSessions,
        averageImprovementScore: avgImprovement?.toFixed(2),
        averageAccuracyRate: avgScore?.toFixed(2),
        averagePracticeStreak: Math.round(avgStreak)
      };
    } catch (err) {
      console.error('Error fetching aggregated stats:', err);
      throw err;
    }
  },

  /**
   * Get pronunciation improvement trend over time
   */
  async getImprovementTrend(filters = {}) {
    try {
      let query = supabase?.from('pronunciation_analytics')?.select('date, average_score, improvement_trend')?.order('date', { ascending: true });

      if (filters?.playId) {
        query = query?.eq('play_id', filters?.playId);
      }

      if (filters?.dateFrom) {
        query = query?.gte('date', filters?.dateFrom);
      }

      if (filters?.dateTo) {
        query = query?.lte('date', filters?.dateTo);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(d => ({
        date: d?.date,
        averageScore: parseFloat(d?.average_score),
        improvementTrend: parseFloat(d?.improvement_trend)
      })) || [];
    } catch (err) {
      console.error('Error fetching improvement trend:', err);
      throw err;
    }
  },

  /**
   * Get AI feedback patterns analysis
   */
  async getAIFeedbackPatterns(filters = {}) {
    try {
      let query = supabase?.from('pronunciation_analytics')?.select('ai_feedback_summary, common_mistakes');

      if (filters?.playId) {
        query = query?.eq('play_id', filters?.playId);
      }

      if (filters?.dateFrom) {
        query = query?.gte('date', filters?.dateFrom);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Aggregate feedback patterns
      const feedbackAggregation = {};
      const mistakesAggregation = {};

      data?.forEach(record => {
        // Aggregate AI feedback
        if (record?.ai_feedback_summary) {
          Object.entries(record?.ai_feedback_summary)?.forEach(([key, value]) => {
            feedbackAggregation[key] = (feedbackAggregation?.[key] || 0) + value;
          });
        }

        // Aggregate common mistakes
        if (record?.common_mistakes && Array.isArray(record?.common_mistakes)) {
          record?.common_mistakes?.forEach(mistake => {
            mistakesAggregation[mistake] = (mistakesAggregation?.[mistake] || 0) + 1;
          });
        }
      });

      return {
        feedbackPatterns: Object.entries(feedbackAggregation)?.map(([key, value]) => ({
          category: key,
          count: value
        })),
        commonMistakes: Object.entries(mistakesAggregation)?.map(([mistake, count]) => ({ mistake, count }))?.sort((a, b) => b?.count - a?.count)?.slice(0, 10)
      };
    } catch (err) {
      console.error('Error fetching AI feedback patterns:', err);
      throw err;
    }
  },

  /**
   * Get practice frequency heatmap data
   */
  async getPracticeFrequency(filters = {}) {
    try {
      let query = supabase?.from('pronunciation_analytics')?.select('date, total_attempts, user_id')?.order('date', { ascending: true });

      if (filters?.playId) {
        query = query?.eq('play_id', filters?.playId);
      }

      if (filters?.dateFrom) {
        query = query?.gte('date', filters?.dateFrom);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Group by date and aggregate attempts
      const frequencyMap = {};
      data?.forEach(record => {
        const date = record?.date;
        if (!frequencyMap?.[date]) {
          frequencyMap[date] = {
            date,
            totalAttempts: 0,
            uniqueUsers: new Set()
          };
        }
        frequencyMap[date].totalAttempts += record?.total_attempts;
        frequencyMap?.[date]?.uniqueUsers?.add(record?.user_id);
      });

      return Object.values(frequencyMap)?.map(item => ({
        date: item?.date,
        totalAttempts: item?.totalAttempts,
        uniqueUsers: item?.uniqueUsers?.size
      }));
    } catch (err) {
      console.error('Error fetching practice frequency:', err);
      throw err;
    }
  },

  /**
   * Get student performance rankings
   */
  async getStudentRankings(filters = {}) {
    try {
      let query = supabase?.from('pronunciation_analytics')?.select(`
          user_id,
          average_score,
          practice_streak,
          improvement_trend,
          total_attempts,
          user_profiles!inner(full_name, email)
        `);

      if (filters?.playId) {
        query = query?.eq('play_id', filters?.playId);
      }

      if (filters?.dateFrom) {
        query = query?.gte('date', filters?.dateFrom);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Aggregate by user
      const userStats = {};
      data?.forEach(record => {
        const userId = record?.user_id;
        if (!userStats?.[userId]) {
          userStats[userId] = {
            userId,
            studentName: record?.user_profiles?.full_name,
            email: record?.user_profiles?.email,
            totalPracticeTime: 0,
            accuracyImprovement: [],
            streakMaintained: 0,
            challengesCompleted: 0
          };
        }
        userStats[userId].totalPracticeTime += record?.total_attempts;
        userStats?.[userId]?.accuracyImprovement?.push(parseFloat(record?.improvement_trend));
        userStats[userId].streakMaintained = Math.max(userStats?.[userId]?.streakMaintained, record?.practice_streak);
        userStats[userId].challengesCompleted += record?.total_attempts;
      });

      // Calculate averages and sort
      return Object.values(userStats)?.map(user => ({
          ...user,
          accuracyImprovement: user?.accuracyImprovement?.length > 0
            ? (user?.accuracyImprovement?.reduce((a, b) => a + b, 0) / user?.accuracyImprovement?.length)?.toFixed(2)
            : '0.00'
        }))?.sort((a, b) => parseFloat(b?.accuracyImprovement) - parseFloat(a?.accuracyImprovement));
    } catch (err) {
      console.error('Error fetching student rankings:', err);
      throw err;
    }
  },

  /**
   * Get available plays for filtering
   */
  async getAvailablePlays() {
    try {
      const { data, error } = await supabase?.from('plays')?.select('id, title')?.order('title');

      if (error) throw error;

      return data?.map(play => ({
        id: play?.id,
        title: play?.title
      })) || [];
    } catch (err) {
      console.error('Error fetching available plays:', err);
      throw err;
    }
  }
};